import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, FileSpreadsheet, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { API_BASE } from "./AuthContext";

/**
 * 4-step wizard:
 *   1. Pick file (CSV) → frontend parses headers + first 200 rows for preview
 *   2. Map columns → target Client fields
 *   3. Preview backend validation (per-row errors / duplicates)
 *   4. Commit
 */

const SAMPLE_CSV = "name,type,email,phone,notes\nAcme Coffee Roasters,business,contact@acme.test,+61 412 000 111,Pulled from Xero\nGreta Lindqvist,individual,greta@example.test,,1040 returnees";

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  // Naive CSV splitter that handles double-quoted cells.
  const splitLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && (i + 1 < line.length) && line[i + 1] === '"') { cur += '"'; i++; continue; }
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1, 5001).map((l) => {
    const cells = splitLine(l);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ""; });
    return row;
  });
  return { headers, rows };
}

// Heuristic header → field auto-mapping
function autoMap(headers, fieldKeys) {
  const map = {};
  for (const h of headers) {
    const lc = h.toLowerCase().trim();
    if (["name", "client", "client name", "company", "business name"].includes(lc)) map[h] = "name";
    else if (["type", "client type", "kind"].includes(lc)) map[h] = "type";
    else if (["email", "e-mail", "primary email", "contact email"].includes(lc)) map[h] = "primary_contact_email";
    else if (["phone", "telephone", "mobile", "contact phone"].includes(lc)) map[h] = "primary_contact_phone";
    else if (["contact name", "primary contact"].includes(lc)) map[h] = "primary_contact_name";
    else if (["status"].includes(lc)) map[h] = "status";
    else if (["notes", "comment", "comments", "memo"].includes(lc)) map[h] = "notes";
    else map[h] = "";
  }
  // de-dup: don't allow same target twice (keep first)
  const used = new Set();
  for (const h of headers) {
    if (!map[h]) continue;
    if (used.has(map[h])) map[h] = "";
    else used.add(map[h]);
  }
  return map;
}

export default function ClientImportWizard({ open, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [fieldDefs, setFieldDefs] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [previewResult, setPreviewResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipDupes, setSkipDupes] = useState(true);
  const [commitResult, setCommitResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_BASE}/clients/import/fields`).then((r) => setFieldDefs(r.data?.fields || [])).catch(() => {});
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setParsed({ headers: [], rows: [] });
      setColumnMap({}); setPreviewResult(null); setCommitResult(null);
    }
  }, [open]);

  const fieldByKey = useMemo(() => Object.fromEntries(fieldDefs.map((f) => [f.key, f])), [fieldDefs]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) {
      toast.error("Please pick a .csv file");
      return;
    }
    setFile(f);
    const text = await f.text();
    const p = parseCSV(text);
    if (p.headers.length === 0 || p.rows.length === 0) {
      toast.error("CSV looks empty");
      return;
    }
    setParsed(p);
    setColumnMap(autoMap(p.headers, fieldDefs.map((d) => d.key)));
    setStep(2);
  };

  const onPasteSample = () => {
    const p = parseCSV(SAMPLE_CSV);
    setFile({ name: "sample.csv" });
    setParsed(p);
    setColumnMap(autoMap(p.headers, fieldDefs.map((d) => d.key)));
    setStep(2);
  };

  const goPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/clients/import/preview`, {
        rows: parsed.rows, column_map: columnMap,
      });
      setPreviewResult(r.data);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const goCommit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/clients/import/commit`, {
        rows: parsed.rows, column_map: columnMap, skip_dupes: skipDupes,
      });
      setCommitResult(r.data);
      setStep(4);
      toast.success(`Imported ${r.data.created_count} client(s)`);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const nameMappedTo = Object.entries(columnMap).find(([, v]) => v === "name");
  const canPreview = !!nameMappedTo;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
      onClick={onClose}
      data-testid="client-import-wizard"
    >
      <div
        className="w-full sm:max-w-3xl max-h-[90vh] overflow-hidden rounded-t-lg sm:rounded-lg shadow-xl flex flex-col"
        style={{ background: "var(--paper)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-[var(--ink-2)]" />
            <div className="font-medium text-[var(--ink)]">Bulk client import — step {step} of 4</div>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="client-import-close">
            <X size={18}/>
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 py-2 border-b text-[11px] text-[var(--ink-3)] flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
          {["Upload", "Map columns", "Preview", "Done"].map((label, i) => (
            <span key={label} className={`px-2 py-0.5 rounded ${step === i + 1 ? "bg-[var(--ink)] text-[var(--paper)]" : ""}`}>
              {i + 1}. {label}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-4" data-testid="import-step-1">
              <div className="border-2 border-dashed rounded-lg p-8 text-center" style={{ borderColor: "var(--border)" }}>
                <Upload size={32} className="mx-auto text-[var(--ink-3)] mb-3"/>
                <div className="text-[14px] text-[var(--ink-2)] mb-2">Pick a CSV file (max 5000 rows)</div>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    onChange={onPickFile}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  <span
                    className="inline-block px-4 py-2 rounded-md text-[13px] font-medium cursor-pointer"
                    style={{ background: "var(--ink)", color: "var(--paper)" }}
                  >
                    Choose file
                  </span>
                </label>
                <div className="text-[11px] text-[var(--ink-3)] mt-3">
                  or <button type="button" onClick={onPasteSample} className="underline" data-testid="import-load-sample">load a 2-row sample</button>
                </div>
              </div>
              <div className="text-[12px] text-[var(--ink-3)]">
                <div className="font-medium text-[var(--ink-2)] mb-1">Recognised headers</div>
                <code className="block text-[11px] bg-[var(--subtle)] rounded px-2 py-1.5 font-mono">name, type, email, phone, contact name, status, notes</code>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="import-step-2">
              <div className="text-[13px] text-[var(--ink-2)]">
                <span className="font-medium">{file?.name}</span> · {parsed.rows.length} row(s) · {parsed.headers.length} column(s).
                Map each column to a target field. <strong>Name</strong> is required.
              </div>
              <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] uppercase tracking-wide" style={{ background: "var(--subtle)", color: "var(--ink-3)" }}>
                    <tr><th className="text-left px-3 py-2">CSV column</th><th className="text-left px-3 py-2">Sample</th><th className="text-left px-3 py-2">→ Target field</th></tr>
                  </thead>
                  <tbody>
                    {parsed.headers.map((h) => (
                      <tr key={h} className="border-t" style={{ borderColor: "var(--border)" }}>
                        <td className="px-3 py-2 font-mono">{h}</td>
                        <td className="px-3 py-2 text-[var(--ink-3)] truncate max-w-[200px]">{parsed.rows[0]?.[h]}</td>
                        <td className="px-3 py-2">
                          <select
                            value={columnMap[h] || ""}
                            onChange={(e) => setColumnMap((m) => ({ ...m, [h]: e.target.value }))}
                            className="px-2 py-1 rounded border bg-transparent text-[12px]"
                            style={{ borderColor: "var(--border)" }}
                            data-testid={`import-map-${h.replace(/\s+/g, '-')}`}
                          >
                            <option value="">— Skip —</option>
                            {fieldDefs.map((d) => (<option key={d.key} value={d.key}>{d.label}{d.required ? " *" : ""}</option>))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!canPreview && (
                <div className="text-[12px] text-[var(--accent)] inline-flex items-center gap-1">
                  <AlertCircle size={12}/> Map at least one column to <strong>Name</strong> to continue.
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setStep(1)} className="px-3 py-2 rounded-md text-[13px] border" style={{ borderColor: "var(--border)" }} data-testid="import-back-1">Back</button>
                <button onClick={goPreview} disabled={!canPreview || busy} className="px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="import-preview">
                  {busy ? "Validating…" : "Preview validation →"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && previewResult && (
            <div className="space-y-4" data-testid="import-step-3">
              <div className="grid grid-cols-4 gap-3 text-center">
                <Stat label="Rows" value={previewResult.total} />
                <Stat label="Valid" value={previewResult.valid} color="#3a7c3a" />
                <Stat label="Duplicates" value={previewResult.duplicates} color="#B91C1C" />
                <Stat label="Invalid" value={previewResult.invalid} color="#a82a2a" />
              </div>
              <div className="border rounded-md overflow-hidden max-h-72 overflow-y-auto" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-[12px]">
                  <thead className="text-[10px] uppercase tracking-wide sticky top-0" style={{ background: "var(--subtle)", color: "var(--ink-3)" }}>
                    <tr>
                      <th className="text-left px-2 py-1.5">#</th>
                      <th className="text-left px-2 py-1.5">Name</th>
                      <th className="text-left px-2 py-1.5">Type</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewResult.issues.slice(0, 200).map((row) => (
                      <tr key={row.row_index} className="border-t" style={{ borderColor: "var(--border)" }}>
                        <td className="px-2 py-1.5 font-mono text-[var(--ink-3)]">{row.row_index + 1}</td>
                        <td className="px-2 py-1.5">{row.name || <em className="text-[var(--ink-3)]">(none)</em>}</td>
                        <td className="px-2 py-1.5">{row.type}</td>
                        <td className="px-2 py-1.5">{row.email}</td>
                        <td className="px-2 py-1.5">
                          {row.errors.length > 0 ? <span className="text-[#a82a2a]">{row.errors.join(", ")}</span> :
                            row.is_duplicate ? <span className="text-[#B91C1C]">Duplicate</span> :
                            <span className="text-[#3a7c3a]">Will create</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} data-testid="import-skip-dupes"/>
                Skip duplicates (rows matching an existing client name — case-insensitive)
              </label>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setStep(2)} className="px-3 py-2 rounded-md text-[13px] border" style={{ borderColor: "var(--border)" }} data-testid="import-back-2">Back</button>
                <button onClick={goCommit} disabled={busy || previewResult.valid === 0} className="px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="import-commit">
                  {busy ? "Importing…" : `Import ${previewResult.valid + (skipDupes ? 0 : previewResult.duplicates)} client(s)`}
                </button>
              </div>
            </div>
          )}

          {step === 4 && commitResult && (
            <div className="space-y-4" data-testid="import-step-4">
              <div className="text-center py-6">
                <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "#3a7c3a" }}/>
                <div className="text-[18px] font-medium text-[var(--ink)]" data-testid="import-result-created">
                  {commitResult.created_count} client(s) imported
                </div>
                <div className="text-[var(--ink-3)] text-[12px] mt-1">
                  {commitResult.skipped_count} skipped
                </div>
              </div>
              {commitResult.skipped.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-[var(--ink-3)] inline-flex items-center gap-1">
                    <Copy size={11}/> Show skipped rows
                  </summary>
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2" style={{ borderColor: "var(--border)" }}>
                    {commitResult.skipped.map((s) => (
                      <div key={`skip-${s.row_index}`} className="text-[11px] text-[var(--ink-3)]">
                        Row {s.row_index + 1}: <strong>{s.reason}</strong>{s.name ? ` (${s.name})` : ""}{s.errors ? ` — ${s.errors.join(", ")}` : ""}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="flex items-center justify-end">
                <button onClick={onClose} className="px-4 py-2 rounded-md text-[13px] font-medium" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="import-done">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)]">{label}</div>
      <div className="text-[24px] font-mono font-medium mt-1" style={{ color: color || "var(--ink)" }}>{value}</div>
    </div>
  );
}
